import React from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { ArrowRight } from '@hhgtech/icons/other'
import { Box, BoxProps } from '@mantine/core'
import { useIntl } from 'react-intl'

import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { AffiliateChanel, ProductAffiliate } from 'interfaces/types/Ecommerce'
import { currencyFormat } from 'utils/ecommerce'

import {
  StyledBuyNowButtons,
  StyledBuyNowItem,
  StyledSeeMoreButton,
} from './index.styled'

export const IdentifierData: {
  [key in AffiliateChanel]?: {
    name: string
    icon: (size: number) => void
  }
} = {
  shopee: {
    name: 'Shopee',
    icon: function Icon(size: number) {
      return (
        <img
          loading="lazy"
          src="/images/vendors/vendor-shopee.png"
          style={{
            width: size,
            height: size,
          }}
          alt="Lazada"
        />
      )
    },
  },
  lazada: {
    name: 'Lazada',
    icon: function Icon(size: number) {
      return (
        <img
          loading="lazy"
          src="/images/vendors/vendor-lazada.png"
          style={{
            width: size,
            height: size,
          }}
          alt="Lazada"
        />
      )
    },
  },
  tiki: {
    name: 'Tiki',
    icon: function Icon(size: number) {
      return (
        <img
          loading="lazy"
          src="/images/vendors/vendor-tiki.png"
          style={{
            width: size,
            height: size,
          }}
          alt="Tiki"
        />
      )
    },
  },
  hellobacsi: {
    name: 'Hellobacsi',
    icon: function Icon(size: number) {
      return (
        <img
          loading="lazy"
          src="/images/vendors/vendor-hellobacsi.png"
          style={{
            width: size,
            height: size,
          }}
          alt="affiliate hellobacsi"
        />
      )
    },
  },
}

export type Props = {
  /**
   * extending data props
   */
  affiliates: ProductAffiliate[]
  size?: 'lg' | 'sm' | 'auto'
  /**
   * props for Box element
   */
} & BoxProps
const BuyNowButtons = ({ affiliates, size = 'auto', ...boxProps }: Props) => {
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()
  const isAuto = size === 'auto'
  const minShow = (isAuto && isMobile) || size === 'sm' ? 4 : 8
  const [showAll, setShowAll] = React.useState(false)
  const hasMore = affiliates.length > minShow
  return (
    <Box {...boxProps}>
      <div
        style={{
          marginBottom: 8,
        }}
      >
        {isMobile ? (
          <Heading tag="h5" as={'p' as any}>
            {f({
              id: 'product.affiliate.buyNow',
              defaultMessage: 'Mua ngay',
            })}
          </Heading>
        ) : (
          <Text size="s4">
            {f({
              id: 'product.affiliate.buyNow',
              defaultMessage: 'Mua ngay',
            })}
          </Text>
        )}
      </div>
      <StyledBuyNowButtons data-size={size} data-one={affiliates?.length <= 1}>
        {affiliates?.slice(0, minShow)?.map(
          (item, index) =>
            IdentifierData[item.channelCode] && (
              <StyledBuyNowItem
                key={item.channelCode + index}
                href={item.url}
                target="_blank"
                data-event-label={`https://${domainLocales[locale]}${item.url}`}
                // data-product-sku={{sku_id}}
                data-event-category="Ecommerce"
                data-event-action="Buy Now Click"
                data-product-channel={
                  item.channelCode.charAt(0).toUpperCase() +
                  item.channelCode.slice(1)
                }
              >
                {IdentifierData[item.channelCode]?.icon(36) || ''}
                <div className="info-text">
                  <Text size="n1">
                    {IdentifierData[item.channelCode]?.name}
                  </Text>
                  <Text
                    className="price-text"
                    size="s4"
                    sx={(theme) => ({ color: theme.colors.red[7] })}
                  >
                    {currencyFormat(item.price, locale, 'VND')}
                    <ArrowRight size={18} />
                    {/* <ChevronRightIcon width="16px" /> */}
                  </Text>
                </div>
              </StyledBuyNowItem>
            ),
        )}
      </StyledBuyNowButtons>
      {hasMore && (
        <>
          <StyledBuyNowButtons
            data-size={size}
            data-more
            data-show-all={String(showAll)}
          >
            {affiliates?.slice(8)?.map(
              (item, index) =>
                IdentifierData[item.channelCode] && (
                  <StyledBuyNowItem
                    key={item.channelCode + index}
                    href={item.url}
                    target="_blank"
                    // data-event-label={{Product URL}}
                    // data-product-sku={{sku_id}}
                    data-event-category="Ecommerce"
                    data-event-action="Shop Click"
                    data-product-channel={
                      item.channelCode.charAt(0).toUpperCase() +
                      item.channelCode.slice(1)
                    }
                  >
                    {IdentifierData[item.channelCode]?.icon(36) || <></>}
                    <div className="info-text">
                      <Text size="n1">
                        {IdentifierData[item.channelCode]?.name}
                      </Text>
                      <Text className="price-text" size="s4">
                        {currencyFormat(item.price, locale, 'VND')}
                        <ArrowRight size={16} />
                      </Text>
                    </div>
                  </StyledBuyNowItem>
                ),
            )}
          </StyledBuyNowButtons>
          <StyledSeeMoreButton
            onClick={() => setShowAll(!showAll)}
            size="sm"
            color="ghost"
          >
            {showAll
              ? f({
                  id: 'common.seeLess',
                  defaultMessage: 'Thu gọn',
                })
              : f(
                  {
                    id: 'product.affiliate.seeMore',
                    defaultMessage: 'Xem thêm {count} sàn',
                  },
                  {
                    count: 123,
                  },
                )}
            <ArrowRight />
            {/* <ChevronRightIcon data-show={showAll} width="16px" /> */}
          </StyledSeeMoreButton>
        </>
      )}
    </Box>
  )
}

export { BuyNowButtons }
